/**
 * Motor de reglas del centro de mando (documento 06a, §5-§6; documento 06b): fase de una
 * experiencia oficial, tareas rápidas, horas hábiles y las consecuencias de aceptar una
 * solicitud o cerrar una experiencia. Funciones puras, sin estado ni red, sin importar Nest,
 * Prisma ni React (ADR-0017) — el llamador ya trae los datos que hagan falta, y ninguna
 * función de aquí consulta la base.
 *
 * Ninguna función llama a `Date.now()` ni construye `new Date()` sin argumentos: la hora
 * entra siempre como parámetro (`ahora`), mismo criterio que `rules/joins.ts`.
 *
 * Ningún texto para humanos: solo claves. Este fichero no importa de `contracts/` (ADR-0017:
 * `rules/` es terreno independiente): las uniones de literales que ya declara
 * `contracts/experienceModeration` se reescriben aquí a mano, con nombres de tipo DISTINTOS a
 * los del contrato para que el barrel `src/index.ts` (que reexporta `contracts/` y `rules/` con
 * `export *`) no choque por un nombre duplicado — mismo criterio que `EstadoParticipacion`
 * frente a `JoinStatus` en `rules/joins.ts`.
 */

import { esExperienciaOficial, type TierCreacionExperiencia } from './experiences';
import { AUSENCIAS_PARA_BLOQUEO, DIAS_DE_BLOQUEO } from './joins';

// -----------------------------------------------------------------------------
// Tipos redeclarados a mano (ADR-0017: `rules/` no importa de `contracts/`).
// -----------------------------------------------------------------------------

/** Los seis estados de "experience" (documento 03a §5.3, `contracts/experiences` `ExperienceStatus`). */
export type EstadoExperienciaFase = 'DRAFT' | 'PENDING_APPROVAL' | 'PUBLISHED' | 'CANCELLED' | 'FINISHED' | 'TRASH';

/** Las siete fases del panel del centro de mando (documento 06a §5.1, `contracts/experienceModeration` `ExperiencePhase`). */
export type FaseExperiencia =
	| 'propuestaRecibida'
	| 'enPreparacion'
	| 'copyListo'
	| 'publicada'
	| 'enCurso'
	| 'terminadaPendienteDeCerrar'
	| 'archivada';

/** Las seis tareas rápidas del panel (documento 06a §5.1, CA-06a.14; `contracts/experienceModeration` `QuickTask`). */
export type TareaRapida = 'revisar' | 'confirmar' | 'publicar' | 'atenderSolicitudes' | 'cerrar' | 'faltaGente';

/** Las cinco etapas del embudo (documento 02a §5.3, `contracts/users` `FunnelState`), repetidas aquí por la misma razón que el resto de tipos de este fichero (ADR-0017). */
export type EtapaEmbudo = 'LEAD' | 'VERIFIED' | 'MEMBER' | 'EXPERIENCE' | 'ADVOCATE';

// -----------------------------------------------------------------------------
// Constantes de negocio.
// -----------------------------------------------------------------------------

/** Jornada laboral en la que corren las horas hábiles (RN-6, CA-06a.13). */
export const JORNADA_INICIO_HORA = 9;
export const JORNADA_FIN_HORA = 18;
export const ZONA_HORARIA_NEGOCIO = 'Europe/Madrid';

/**
 * Umbral y ventana de "falta gente" (documento 06b §10.1) — **CONFIRMADOS POR EL CLIENTE**: se
 * implementaron con la lectura del ejemplo del documento 06b §10.1 («faltan 11 plazas y quedan
 * 6 días» sobre una experiencia de 15 plazas con 4 confirmadas: 4/15 ≈ 27 % de cobertura, muy
 * por debajo de la mitad, y 6 días es menos de una semana), y el cliente confirmó los dos
 * números **sin cambios** el 17/09/2026 (P-20, `respuestas-tory-2026-09-17/respuestas.md`: «50 %
 * y 7 días — ✅ Confirmados los dos. Están bien elegidos»), repetido el 18/09/2026
 * (`para-tory-2026-09-18/0-RESPUESTA-AL-RECUENTO.md`). Aisladas en su propia función
 * (`faltaGente`, más abajo) para que, si el cliente pide otro criterio más adelante, cambiarlo
 * sea una línea, sin tocar `tareasRapidas` ni ningún llamador — mismo patrón que
 * `loIntentoYNoPudo` en `rules/joins.ts`.
 */
export const UMBRAL_FALTA_GENTE_PCT = 50;
export const DIAS_AVISO_FALTA_GENTE = 7;

// Building blocks de conversión de tiempo (Biome `noMagicNumbers`), mismo patrón que
// `rules/joins.ts`.
const SEGUNDOS_POR_MINUTO = 60;
const MINUTOS_POR_HORA = 60;
const HORAS_POR_DIA = 24;
const MILISEGUNDOS_POR_SEGUNDO = 1000;
const MS_POR_HORA = MINUTOS_POR_HORA * SEGUNDOS_POR_MINUTO * MILISEGUNDOS_POR_SEGUNDO;
const MS_POR_DIA = HORAS_POR_DIA * MS_POR_HORA;

/** La última hora, minuto, segundo y milisegundo de un día natural — el final del día. */
const ULTIMA_HORA_DEL_DIA = 23;
const ULTIMO_MINUTO_U_SEGUNDO = 59;
const ULTIMO_MILISEGUNDO = 999;

/** El 100 % de cobertura de aforo — ni valor por defecto arbitrario ni número mágico suelto. */
const PORCENTAJE_COMPLETO = 100;

// -----------------------------------------------------------------------------
// Aritmética de zona horaria sin librería: convertir una fecha/hora "de pared" en
// `ZONA_HORARIA_NEGOCIO` a su instante UTC real, y leer la fecha local de un instante — ambas
// vía `Intl.DateTimeFormat` con `timeZone`, nunca con `getHours()`/`getDay()` del sistema
// (imprescindible porque el proceso puede correr en cualquier zona horaria, y porque
// Europe/Madrid cambia de offset con el horario de verano).
// -----------------------------------------------------------------------------

interface FechaLocal {
	anio: number;
	dia: number;
	mes: number;
}

/** La fecha (año, mes, día), tal y como se lee en `zona`, del instante `fecha`. */
const fechaLocalDe = (fecha: Date, zona: string): FechaLocal => {
	const partes = new Intl.DateTimeFormat('en-CA', {
		day: '2-digit',
		month: '2-digit',
		timeZone: zona,
		year: 'numeric',
	}).formatToParts(fecha);
	return {
		anio: Number(partes.find(parte => parte.type === 'year')?.value),
		dia: Number(partes.find(parte => parte.type === 'day')?.value),
		mes: Number(partes.find(parte => parte.type === 'month')?.value),
	};
};

/** El día natural siguiente a `fecha`, en calendario gregoriano puro (sin zona: solo aritmética de fechas). */
const diaSiguiente = (fecha: FechaLocal): FechaLocal => {
	const instante = new Date(Date.UTC(fecha.anio, fecha.mes - 1, fecha.dia + 1));
	return { anio: instante.getUTCFullYear(), dia: instante.getUTCDate(), mes: instante.getUTCMonth() + 1 };
};

/**
 * Si `fecha` cae en fin de semana. El día de la semana de una fecha de calendario (año, mes,
 * día) no depende de ninguna zona horaria, así que basta `Date.UTC` + `getUTCDay()` sobre los
 * tres números ya extraídos en la zona de negocio.
 */
const esFinDeSemana = (fecha: FechaLocal): boolean => {
	const diaSemana = new Date(Date.UTC(fecha.anio, fecha.mes - 1, fecha.dia)).getUTCDay();
	const DOMINGO = 0;
	const SABADO = 6;
	return diaSemana === DOMINGO || diaSemana === SABADO;
};

/**
 * El instante UTC real que corresponde a las `hora:minuto:segundo.ms` de `fecha`, leídas como
 * hora local de `zona` (algoritmo de dos pasadas, sin librería): primero se asume que esos
 * campos ya son UTC; después se lee, con `Intl.DateTimeFormat`, qué hora local marca `zona` en
 * ese instante provisional, y la diferencia con la hora pedida es exactamente el offset que
 * hay que corregir — funciona igual en horario de invierno que de verano, porque el offset se
 * consulta para ese instante concreto, nunca se asume fijo.
 *
 * El offset se mide SIEMPRE con `ms` fuera de las dos fechas que se restan (`comoSiFueraUtc` y
 * `comoSiFueraLocal`), a propósito: `Intl.DateTimeFormat` no expone milisegundos (su parte
 * `second` es la única con esa resolución), así que `comoSiFueraLocal` —construida a partir de
 * las partes que devuelve el formateador— nunca puede llevarlos. Si `comoSiFueraUtc` sí los
 * llevara, la resta quedaría entre dos instantes de precisión distinta y el offset saldría
 * desviado por esos mismos milisegundos —el defecto ya documentado en `moderationRules.test.ts`—.
 * Por eso `ms` se aplica una única vez, al final, sobre el resultado ya corregido de offset: es
 * una suma, no una entrada más del cálculo.
 */
const instanteEnZona = (
	fecha: FechaLocal,
	hora: number,
	minuto: number,
	segundo: number,
	ms: number,
	zona: string,
): Date => {
	const comoSiFueraUtc = Date.UTC(fecha.anio, fecha.mes - 1, fecha.dia, hora, minuto, segundo);
	const formateador = new Intl.DateTimeFormat('en-US', {
		day: '2-digit',
		hour: '2-digit',
		hour12: false,
		minute: '2-digit',
		month: '2-digit',
		second: '2-digit',
		timeZone: zona,
		year: 'numeric',
	});
	const partes = Object.fromEntries(
		formateador.formatToParts(new Date(comoSiFueraUtc)).map(parte => [parte.type, parte.value]),
	);
	// `hour12: false` puede devolver "24" para la medianoche en algunos motores ICU; se normaliza a 0.
	const horaLocalLeida = Number(partes.hour) % HORAS_POR_DIA;
	const comoSiFueraLocal = Date.UTC(
		Number(partes.year),
		Number(partes.month) - 1,
		Number(partes.day),
		horaLocalLeida,
		Number(partes.minute),
		Number(partes.second),
	);
	const offsetMs = comoSiFueraLocal - comoSiFueraUtc;
	return new Date(comoSiFueraUtc - offsetMs + ms);
};

// -----------------------------------------------------------------------------
// faseExperiencia — las siete fases derivadas de `status` y los dos sellos (documento 06a §5.1).
// -----------------------------------------------------------------------------

export interface ContextoFaseExperiencia {
	ahora: Date;
	/** Momento en que se puso el segundo sello ("copy listo"). `undefined` = sello sin poner. */
	confirmedAt?: Date;
	/** `null`/`undefined` según el resto de DTOs de "experience": sin fecha de fin. */
	endDate?: Date | null;
	/** Momento en que se puso el primer sello ("revisado"). `undefined` = sello sin poner. */
	reviewedAt?: Date;
	startDate?: Date | null;
	status: EstadoExperienciaFase;
}

/**
 * Deriva la fase de una experiencia (documento 06a §5.1): `FINISHED` es siempre `archivada`.
 * `PUBLISHED` se subdivide en tres por fecha: `terminadaPendienteDeCerrar` si el final ya pasó,
 * `enCurso` si `ahora` cae entre `startDate` y ese final, y `publicada` en cualquier otro caso.
 * `PENDING_APPROVAL` (y `DRAFT`, ver abajo) se subdivide en tres por los dos sellos: `copyListo`
 * con los dos puestos, `enPreparacion` con solo el primero (`reviewedAt`), `propuestaRecibida`
 * sin ninguno.
 *
 * `DRAFT` se lee como `propuestaRecibida`: el documento 03c ya explica que "experience" puede
 * nacer en `DRAFT` (el borrador guardado en servidor antes de enviarse) y ese borrador, una vez
 * entra al panel, todavía no ha recibido ninguna revisión — mismo punto de partida que una
 * `PENDING_APPROVAL` recién llegada, sin sellos. `CANCELLED` y `TRASH` se leen como `archivada`:
 * ninguna de las dos sigue viva para el panel, igual que `FINISHED` — la experiencia ya no pide
 * ninguna acción de moderación, solo queda archivada.
 *
 * Sin `endDate`: si hay `startDate`, se usa como final el último instante de ese mismo día
 * natural (23:59:59.999, hora local de `ZONA_HORARIA_NEGOCIO`) — una experiencia de un día sin
 * hora de cierre declarada no debe quedarse "en curso" para siempre. Sin `startDate` tampoco, la
 * experiencia no puede estar `enCurso` ni `terminadaPendienteDeCerrar`: sin ninguna fecha no hay
 * nada que comparar contra `ahora`, así que una `PUBLISHED` sin fechas cae en `publicada`.
 */
export const faseExperiencia = (contexto: ContextoFaseExperiencia): FaseExperiencia => {
	if (contexto.status === 'FINISHED' || contexto.status === 'CANCELLED' || contexto.status === 'TRASH') {
		return 'archivada';
	}
	if (contexto.status === 'PUBLISHED') {
		return faseDePublicada(contexto);
	}
	// PENDING_APPROVAL y DRAFT comparten el mismo árbol de sellos.
	if (contexto.reviewedAt !== undefined && contexto.confirmedAt !== undefined) {
		return 'copyListo';
	}
	if (contexto.reviewedAt !== undefined) {
		return 'enPreparacion';
	}
	return 'propuestaRecibida';
};

const faseDePublicada = (
	contexto: Pick<ContextoFaseExperiencia, 'ahora' | 'endDate' | 'startDate'>,
): FaseExperiencia => {
	const finEfectivo = finDelDiaSiHaceFalta(contexto.endDate, contexto.startDate);
	if (finEfectivo !== null && finEfectivo.getTime() < contexto.ahora.getTime()) {
		return 'terminadaPendienteDeCerrar';
	}
	if (
		contexto.startDate !== undefined
		&& contexto.startDate !== null
		&& finEfectivo !== null
		&& contexto.ahora.getTime() >= contexto.startDate.getTime()
		&& contexto.ahora.getTime() <= finEfectivo.getTime()
	) {
		return 'enCurso';
	}
	return 'publicada';
};

/**
 * `endDate` si existe; si no, `startDate` llevado al último instante de ese mismo día natural
 * (23:59:59.999, hora local de `ZONA_HORARIA_NEGOCIO`). `null` si no hay ninguna fecha con la
 * que trabajar.
 */
const finDelDiaSiHaceFalta = (endDate: Date | null | undefined, startDate: Date | null | undefined): Date | null => {
	if (endDate !== undefined && endDate !== null) {
		return endDate;
	}
	if (startDate === undefined || startDate === null) {
		return null;
	}
	const fecha = fechaLocalDe(startDate, ZONA_HORARIA_NEGOCIO);
	return instanteEnZona(
		fecha,
		ULTIMA_HORA_DEL_DIA,
		ULTIMO_MINUTO_U_SEGUNDO,
		ULTIMO_MINUTO_U_SEGUNDO,
		ULTIMO_MILISEGUNDO,
		ZONA_HORARIA_NEGOCIO,
	);
};

// -----------------------------------------------------------------------------
// horasHabilesEntre — horas de jornada laboral entre dos instantes (CA-06a.13).
// -----------------------------------------------------------------------------

/**
 * Horas de jornada (09:00-18:00, lunes a viernes) transcurridas entre `desde` y `hasta`, en
 * `ZONA_HORARIA_NEGOCIO` (CA-06a.13): recorre día natural a día natural el rango —nunca minuto
 * a minuto—, y por cada día laborable suma la intersección entre su jornada (09:00-18:00 local)
 * y `[desde, hasta]`. CA-06a.13 es su prueba de fuego: viernes 18:00 → lunes 10:00 debe dar ~1
 * (nada el viernes tras las 18:00, nada el fin de semana, una hora el lunes de 9 a 10). Nunca
 * negativo: si `hasta` no es posterior a `desde`, devuelve 0.
 */
export const horasHabilesEntre = (desde: Date, hasta: Date): number => {
	if (hasta.getTime() <= desde.getTime()) {
		return 0;
	}
	const fechaFinal = fechaLocalDe(hasta, ZONA_HORARIA_NEGOCIO);
	let cursor = fechaLocalDe(desde, ZONA_HORARIA_NEGOCIO);
	let horas = 0;
	const SIN_MINUTOS_SEGUNDOS_NI_MS = 0;
	for (;;) {
		if (!esFinDeSemana(cursor)) {
			const inicioJornada = instanteEnZona(
				cursor,
				JORNADA_INICIO_HORA,
				SIN_MINUTOS_SEGUNDOS_NI_MS,
				SIN_MINUTOS_SEGUNDOS_NI_MS,
				SIN_MINUTOS_SEGUNDOS_NI_MS,
				ZONA_HORARIA_NEGOCIO,
			);
			const finJornada = instanteEnZona(
				cursor,
				JORNADA_FIN_HORA,
				SIN_MINUTOS_SEGUNDOS_NI_MS,
				SIN_MINUTOS_SEGUNDOS_NI_MS,
				SIN_MINUTOS_SEGUNDOS_NI_MS,
				ZONA_HORARIA_NEGOCIO,
			);
			const inicioTramo = Math.max(inicioJornada.getTime(), desde.getTime());
			const finTramo = Math.min(finJornada.getTime(), hasta.getTime());
			if (finTramo > inicioTramo) {
				horas += (finTramo - inicioTramo) / MS_POR_HORA;
			}
		}
		if (cursor.anio === fechaFinal.anio && cursor.mes === fechaFinal.mes && cursor.dia === fechaFinal.dia) {
			break;
		}
		cursor = diaSiguiente(cursor);
	}
	return horas;
};

// -----------------------------------------------------------------------------
// faltaGente — documento 06b §10.1, aislada a propósito.
// -----------------------------------------------------------------------------

export interface ContextoFaltaGente {
	ahora: Date;
	capacity: number | null;
	confirmedCount: number;
	startDate: Date | null;
}

/**
 * Si a esta experiencia "le falta gente" (documento 06b §10.1) — **CONFIRMADO POR EL CLIENTE**:
 * el umbral y la ventana están implementados con la lectura del ejemplo del documento 06b §10.1
 * («faltan 11 plazas y quedan 6 días», sobre una experiencia de 15 con 4 confirmadas), y el
 * cliente los confirmó sin cambios el 17/09/2026 (P-20, ver el JSDoc de
 * `UMBRAL_FALTA_GENTE_PCT`/`DIAS_AVISO_FALTA_GENTE` más arriba). Cierto cuando hay `capacity`,
 * la cobertura (`confirmedCount / capacity`) está por debajo de `UMBRAL_FALTA_GENTE_PCT` y
 * faltan menos de `DIAS_AVISO_FALTA_GENTE` días para `startDate`. Sin `capacity` o sin
 * `startDate`, falso — sin aforo no hay cobertura que medir, y sin fecha no hay cuenta atrás.
 */
export const faltaGente = (contexto: ContextoFaltaGente): boolean => {
	if (contexto.capacity === null || contexto.startDate === null) {
		return false;
	}
	const coberturaPct = (contexto.confirmedCount / contexto.capacity) * PORCENTAJE_COMPLETO;
	if (coberturaPct >= UMBRAL_FALTA_GENTE_PCT) {
		return false;
	}
	const diasHastaInicio = (contexto.startDate.getTime() - contexto.ahora.getTime()) / MS_POR_DIA;
	return diasHastaInicio < DIAS_AVISO_FALTA_GENTE;
};

// -----------------------------------------------------------------------------
// tareasRapidas — las tareas del panel para una experiencia (documento 06a §5.1, CA-06a.14).
// -----------------------------------------------------------------------------

export interface ContextoTareasRapidas {
	ahora: Date;
	capacity: number | null;
	confirmedCount: number;
	fase: FaseExperiencia;
	pendingRequests: number;
	startDate: Date | null;
}

/**
 * Las tareas rápidas de una experiencia (documento 06a §5.1, CA-06a.14: no aparece `publicar`
 * si ya está publicada — `publicar` solo sale de `fase === 'copyListo'`, nunca de un estado ya
 * publicado). Puede devolver varias a la vez: `atenderSolicitudes` y `faltaGente` no dependen de
 * la fase, así que conviven con cualquiera de las otras.
 */
export const tareasRapidas = (contexto: ContextoTareasRapidas): TareaRapida[] => {
	const tareas: TareaRapida[] = [];
	if (contexto.fase === 'propuestaRecibida') {
		tareas.push('revisar');
	}
	if (contexto.fase === 'enPreparacion') {
		tareas.push('confirmar');
	}
	if (contexto.fase === 'copyListo') {
		tareas.push('publicar');
	}
	if (contexto.pendingRequests > 0) {
		tareas.push('atenderSolicitudes');
	}
	if (contexto.fase === 'terminadaPendienteDeCerrar') {
		tareas.push('cerrar');
	}
	if (
		faltaGente({
			ahora: contexto.ahora,
			capacity: contexto.capacity,
			confirmedCount: contexto.confirmedCount,
			startDate: contexto.startDate,
		})
	) {
		tareas.push('faltaGente');
	}
	return tareas;
};

// -----------------------------------------------------------------------------
// tareasQueVisorPuedeCompletar — no ofrecer una tarea rápida que el servidor va a rechazar
// (documento 06a §5.1, CA-06a.1): `tareasRapidas` decide QUÉ tareas tiene una experiencia a
// partir de su fase; esta función decide, de esas, cuáles puede completar de verdad QUIEN MIRA.
// -----------------------------------------------------------------------------

/**
 * Quién mira el panel, en los dos únicos términos que hacen falta para decidir qué tareas puede
 * completar de verdad — mismos dos nombres que ya usa `ExperienceModerationService.autorizarSobreExperiencia`
 * en el backend (`esAdmin`, `esOrganizador`), para que las dos mitades de la misma regla se lean
 * como la misma cosa en los dos lados.
 */
export interface VisorTareasRapidas {
	esAdmin: boolean;
	esOrganizador: boolean;
}

/**
 * `revisar`/`confirmar` (`PATCH .../review`) y `publicar` (`POST .../publish`) exigen `ADMIN` en
 * el propio backend: ni siquiera quien organiza la experiencia las completa, responde 403 igual
 * que cualquier otra persona (`ExperienceModerationController`, `@Roles(ADMIN)`).
 */
const TAREAS_SOLO_ADMIN: ReadonlySet<TareaRapida> = new Set(['revisar', 'confirmar', 'publicar']);

/**
 * `atenderSolicitudes` (aceptar/rechazar) y `cerrar` los autoriza también quien organiza la
 * experiencia, no solo `ADMIN` (`autorizarSobreExperiencia`, `esAdmin || esOrganizador`).
 */
const TAREAS_ADMIN_U_ORGANIZADOR: ReadonlySet<TareaRapida> = new Set(['atenderSolicitudes', 'cerrar']);

/**
 * Filtra `tareas` a las que `visor` puede completar de verdad: antes de esta función, el panel
 * ofrecía "Revisar"/"Confirmar"/"Publicar" a cualquiera con acceso a la fila (un colaborador que
 * organiza su propia oficial incluido), y el servidor respondía 403 al pulsar, sin ninguna
 * explicación previa. `faltaGente` no aparece en ninguno de los dos conjuntos de arriba —no es una
 * acción, ninguna fila pinta un botón para ella— así que pasa siempre, igual que cualquier tarea
 * futura que tampoco necesite permiso especial.
 */
export const tareasQueVisorPuedeCompletar = (tareas: TareaRapida[], visor: VisorTareasRapidas): TareaRapida[] =>
	tareas.filter(tarea => {
		if (TAREAS_SOLO_ADMIN.has(tarea)) {
			return visor.esAdmin;
		}
		if (TAREAS_ADMIN_U_ORGANIZADOR.has(tarea)) {
			return visor.esAdmin || visor.esOrganizador;
		}
		return true;
	});

// -----------------------------------------------------------------------------
// ascensoTrasAceptar — CA-06a.7 y CA-06a.8: el ascenso de quien acaba de entrar a una oficial.
// -----------------------------------------------------------------------------

export interface ContextoAscensoTrasAceptar {
	accessLabel: 'BRAND' | 'COLLABORATOR' | 'MEMBERS_ONLY' | 'OFFICIAL' | 'PRIVATE';
	funnelState: EtapaEmbudo;
	tier: TierCreacionExperiencia;
}

export interface ResultadoAscensoTrasAceptar {
	funnelState?: 'VERIFIED';
	tier?: 'GRUMETE';
}

/**
 * El ascenso al aceptar una solicitud de una experiencia oficial (CA-06a.7, CA-06a.8). Solo
 * aplica si `esExperienciaOficial(accessLabel)`: aceptar una solicitud de un plan que no pasa
 * por el ciclo de solicitud no ascendía a nadie. `tier` sube a `GRUMETE` solo si hoy es
 * `PENDING`; `funnelState` sube a `VERIFIED` solo si hoy es `LEAD` (RN-3 de la iteración 02: el
 * embudo no retrocede jamás, así que nunca se "baja" ni se toca si ya está más adelante).
 * Idempotente (CL-06a.7): aplicarlo dos veces, con el resultado de la primera ya escrito, no
 * cambia nada — la segunda vez ni `tier` es `PENDING` ni `funnelState` es `LEAD`, así que ambas
 * condiciones fallan y se devuelve un objeto vacío.
 */
export const ascensoTrasAceptar = (contexto: ContextoAscensoTrasAceptar): ResultadoAscensoTrasAceptar => {
	if (!esExperienciaOficial(contexto.accessLabel)) {
		return {};
	}
	const resultado: ResultadoAscensoTrasAceptar = {};
	if (contexto.tier === 'PENDING') {
		resultado.tier = 'GRUMETE';
	}
	if (contexto.funnelState === 'LEAD') {
		resultado.funnelState = 'VERIFIED';
	}
	return resultado;
};

// -----------------------------------------------------------------------------
// bloqueoTrasCierre — CA-06a.12: hasta cuándo queda bloqueada una cuenta al cerrar.
// -----------------------------------------------------------------------------

/**
 * Hasta cuándo queda bloqueada una cuenta al cerrar una experiencia (CA-06a.12), si
 * `ausenciasTotales` alcanza `AUSENCIAS_PARA_BLOQUEO` (constante ya de `rules/joins.ts`, no se
 * declara una propia aquí): `DIAS_DE_BLOQUEO` (también de `rules/joins.ts`) desde `ahora`. Por
 * debajo del umbral, `null` — todavía no toca bloquear.
 */
export const bloqueoTrasCierre = (ausenciasTotales: number, ahora: Date): Date | null => {
	if (ausenciasTotales < AUSENCIAS_PARA_BLOQUEO) {
		return null;
	}
	return new Date(ahora.getTime() + DIAS_DE_BLOQUEO * MS_POR_DIA);
};
